import clsx from "clsx";
import { FiPlusCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { formatPrice } from "../../utils/formatPrice";
import { useStatus } from "../common/hooks/useStatus";
import { formatDate } from "../../utils/dateFormatter";
import zownlogo from "../../assets/zown-logo-icon-blue.svg";
import type { ListingGraphs } from "../../interfaces/graphs";
import type { ListingDetailed } from "../../interfaces/listing";
import calculatorIcon from "../../assets/icons/calculatorIcon.svg";
import { formatPropertyType } from "../../utils/formatPropertyType";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { usePropertyStore } from "../../stores/newStores/cartComparisonStore";
import {
  BathroomIcon,
  BedroomIcon,
  HomeIcon,
  ParkingIcon,
  SizeIcon,
} from "../../assets/BrowseListing";

interface DetailsHomeProps {
  listing: ListingDetailed | null;
  statistics: ListingGraphs | null;
}

const DetailsHome = ({ listing, statistics }: DetailsHomeProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [addComparison, setAddComparison] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const navigate = useNavigate();

  const {
    setFilteredPropertiesCompare,
    filteredPropertiesCompare,
    setCompareListId,
  } = usePropertyStore();

  useEffect(() => {
    const checkOverflow = () => {
      const paragraph = paragraphRef.current;

      if (paragraph) {
        setIsOverflowing(paragraph.scrollHeight > paragraph.clientHeight);
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  const handleAdd = () => {
    if (!listing) {
      return;
    }
    setCompareListId((prev) => {
      const uniqueIds = new Set([...prev, listing.listingId]);
      return [...uniqueIds];
    });

    const isAlreadyInComparison = filteredPropertiesCompare.some(
      (property) => property.listingId === listing.listingId
    );

    if (!isAlreadyInComparison) {
      setFilteredPropertiesCompare((prev) => [
        ...prev,
        { listingId: listing.listingId },
      ]);
    }
  };

  const isListingInComparison = filteredPropertiesCompare.some(
    (property) => property.listingId === listing?.listingId
  );

  const status = useStatus(listing?.status);

  const getValueClassification = (
    price: number,
    valuation: number
  ): { label: string; className: string } => {
    const difference = price - valuation;

    if (difference > 100000) {
      return { label: "OVERVALUED", className: "bg-[#FF5757]" };
    }
    if (difference < -100000) {
      return { label: "UNDERVALUED", className: "bg-[#279E25]" };
    }
    return { label: "FAIR VALUE", className: "bg-[#64A4F8]" };
  };

  const valuation =
    statistics?.zownForecast?.final_ai_eval_for_target_property ?? 0;

  const { label: classification, className } = getValueClassification(
    convertToFormattedAmount(listing?.listPrice) || 0,
    valuation
  );
  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <div className="text-z-shark text-3xl font-bold">
            ${formatPrice(convertToFormattedAmount(listing?.listPrice))}
          </div>
          {listing?.address && (
            <div className="font-normal text-[#333333] pt-2">
              {listing?.address}
            </div>
          )}
        </div>
        <div className="flex space-x-3 flex-col items-end gap-2">
          {listing?.listDate && (
            <div className="bg-z-dark-moss-green rounded-full text-white px-[14px] h-[36px] flex items-center justify-center">
              {formatDate(listing?.listDate)} on Zown
            </div>
          )}
          {listing?.monthlyMortgage && (
            <div className="flex items-center space-x-2">
              <img src={calculatorIcon} alt="calculator icon" />
              <span className="text-xs">
                Est. Mortgage:{" "}
                <span className="underline">
                  $
                  {formatPrice(
                    convertToFormattedAmount(listing.monthlyMortgage)
                  )}
                  /mo
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        {statistics?.zownForecast ? (
          <div className="space-x-3 flex items-center">
            {statistics?.zownForecast.final_ai_eval_for_target_property &&
              listing?.listPrice && (
                <>
                  <div
                    className={`text-sm font-bold text-white w-fit rounded-md px-3 py-1 ${className}`}
                  >
                    {classification}
                  </div>
                  <div className="flex items-center">
                    <img className="w-9" src={zownlogo} alt="zown logo" />
                    <div className="text-[#676473]">
                      Est: $
                      {formatPrice(
                        statistics?.zownForecast
                          .final_ai_eval_for_target_property ?? 0
                      )}
                    </div>
                  </div>
                </>
              )}
          </div>
        ) : (
          <div />
        )}
        <div className="flex flex-col items-center cursor-pointer">
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
          <div
            className={clsx(
              "flex items-center space-x-2 rounded-full py-2 px-3",
              isListingInComparison
                ? "bg-z-dark-moss-green text-white"
                : "text-z-shark"
            )}
            onClick={() => {
              if (isListingInComparison) {
                setAddComparison(true);
              } else {
                setAddComparison(() => true);
                handleAdd();
              }
            }}
          >
            <FiPlusCircle className="text-2xl" />
            <span className="">
              {isListingInComparison
                ? "Added to comparison"
                : "Add to comparison"}
            </span>
          </div>
          <div className="pt-1 text-sm font-bold h-2">
            {isListingInComparison && (
              // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
              <span onClick={() => navigate("/compare")}>View Comparison</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex space-x-2 mt-6">
        <div className="relative flex pt-1">
          <span
            style={{ background: status.color }}
            className={clsx(
              "animate-ping absolute inline-flex h-4 w-4 rounded-full opacity-75"
            )}
          />
          <span
            style={{ background: status.color }}
            className={clsx("relative inline-flex rounded-full h-4 w-4")}
          />
        </div>
        <div className="flex flex-col items-start">
          <div className="font-bold text-z-shark">{status.state}</div>
          {status.desc !== "" && (
            <span className="text-z-shark text-xs">{status.desc}</span>
          )}
        </div>
      </div>
      <div className="grid grid-flow-row-dense grid-cols-5 divide-x-[1px] divide-[#DEDEDE] mt-[52px]">
        <div className="flex flex-col items-center justify-center text-z-shark">
          <img src={HomeIcon} alt="home icon" />
          {listing?.propertyType && (
            <div className="mt-4 mb-[2px] text-center font-semibold">
              {formatPropertyType(listing?.propertyType)}
            </div>
          )}
          <div className="text-sm">Property Type</div>
        </div>
        <div className="flex flex-col items-center justify-center text-z-shark">
          <img src={BedroomIcon} alt="bedroom icon" />
          <div className="mt-4 mb-[2px] font-semibold">{listing?.bed}</div>
          <div className="text-sm">Bedrooms</div>
        </div>
        <div className="flex flex-col items-center justify-center text-z-shark">
          <img src={BathroomIcon} alt="bathroon icon" />
          <div className="mt-4 mb-[2px] font-semibold">{listing?.bath}</div>
          <div className="text-sm">Bathrooms</div>
        </div>
        <div className="flex flex-col items-center justify-center text-z-shark">
          <img src={ParkingIcon} alt="parking icon" />
          <div className="mt-4 mb-[2px] font-semibold">{listing?.parking}</div>
          <div className="text-sm">Parking</div>
        </div>
        <div className="flex flex-col items-center justify-center text-z-shark">
          <img src={SizeIcon} alt="size icon" />
          <div className="mt-4 mb-[2px] font-semibold">
            {listing?.sqft ? `${listing?.sqft} sqft` : "N/A"}
          </div>
          <div className="text-sm">Size</div>
        </div>
      </div>
      <div className="mt-10">
        <div
          ref={paragraphRef}
          className={`leading-8 text-[#7A7674] overflow-hidden ${
            isExpanded ? "max-h-none" : "max-h-[100px] line-clamp-3"
          } transition-all duration-300 ease-in-out`}
        >
          {listing?.description}
        </div>
        {isOverflowing && (
          <span
            onClick={() => setIsExpanded(!isExpanded)}
            onKeyDown={() => setIsExpanded(!isExpanded)}
            className="text-[#7A7674] cursor-pointer mt-2 flex items-end justify-end"
          >
            {isExpanded ? "View less" : "View more"}
          </span>
        )}
      </div>
      <div className="text-[#7A7674] font-light mt-6">
        Listed by:{" "}
        {listing?.agent ? listing.agent : listing?.office.brokerageName}
      </div>
    </div>
  );
};

export { DetailsHome };
